'use client';

import {
  useCallback,
  useRef,
  useContext,
  useEffect,
  useState,
  type FC,
} from 'react';
import {
  Button,
  ButtonGroup,
  Checkbox,
  Dialog,
  Heading,
  Text,
  Flex,
} from '@/_components';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { uploadToStorage } from '@/_api/client-documents';
import { validateFile } from '@/_utils/files';
import { getPdfPageCountFromFile } from '@/_utils/pdfs';
import { isEmpty } from '@/_utils/strings';
import styles from './MultiFormUploadConfirmationDialog.module.css';
import { ExtractedDocumentItem } from '@/_types/models/documents';
import type { MultiFormUploadConfirmationDialogProps } from './MultiFormUploadConfirmationDialog.types';

export const MultiFormUploadConfirmationDialog: FC<
  MultiFormUploadConfirmationDialogProps
> = ({
  filesUploaded,
  isOpen,
  onClose,
  setUploading,
  questionnaire,
  reloadQuestionnairePreview,
}) => {
  const [filesAsHash, setFilesAsHash] = useState({});

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);

  useEffect(() => {
    const initialHash = filesUploaded.reduce((acc: any, file: File) => {
      acc[file.name] = { file, split: false };
      return acc;
    }, {});

    setFilesAsHash(initialHash);
  }, [filesUploaded]);

  const refreshQuestionnaireUntilNoDocumentsAreProcessing = useCallback(
    async (sleep: number, documentNamesBeingProcessed: Array<string>) => {
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      const timeStart: number = Date.now();
      let documentsProcessing: boolean = true;

      while (documentsProcessing && Date.now() - timeStart < 120000) {
        if (signal.aborted) {
          break;
        }

        const questionnaireResponse = await reloadQuestionnairePreview(
          undefined,
          undefined,
          false,
          true,
        );
        const bulkDocuments: Array<ExtractedDocumentItem> =
          questionnaireResponse?.extracted_documents?.filter(
            (doc: ExtractedDocumentItem) =>
              new Date(doc.created_at).getTime() >=
              // 2.2 because 2 minutes + a little buffer
              Date.now() - 2.2 * 60 * 1000,
          ) || [];

        const documentsArePresent = bulkDocuments
          .map(
            (doc: ExtractedDocumentItem) => doc.source?.split('/')?.pop() || '',
          )
          .some(
            (name: string) =>
              !!name && documentNamesBeingProcessed.includes(name),
          );

        if (
          documentsArePresent &&
          !bulkDocuments.some(
            (doc: ExtractedDocumentItem) => doc.status === 'processing',
          )
        ) {
          documentsProcessing = false;
          addToast('success', 'Document(s) finished being processed');
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, sleep));
      }

      if (documentsProcessing) {
        addToast('info', 'Document(s) are still processing');
      }
    },
    [addToast, reloadQuestionnairePreview],
  );

  const onUpload = useCallback(
    async (uploadedFiles: Array<any>) => {
      setUploading(true);

      let path = questionnaire?.document_path!;

      if (!isEmpty(path)) {
        path = path
          .replace('{year}', questionnaire?.tax_year?.toString()!)
          .replace('{section}', 'Bulk Uploads')
          .replace('{questionnaire_name}', questionnaire?.name!);
      }

      const documentNamesBeingProcessed = [];

      try {
        for (const { file, split } of uploadedFiles) {
          const { isValid } = await validateFile(file);

          if (!isValid) {
            addToast(
              'error',
              'We were unable to process some of uploaded files as they appear to be INVALID. Kindly check those files and try uploading them again.',
            );
            continue;
          }

          // TODO: Fix this. Not working properly
          // if (isProtectedPDF) {
          //   addToast(
          //     'error',
          //     'The PDFs you uploaded are either password-protected or cannot be opened. Please either remove the password or upload alternative files.',
          //   );
          //   continue;
          // }

          if (file.name.toLowerCase().endsWith('.pdf')) {
            try {
              const pageCount = await getPdfPageCountFromFile(file);
              const estimatedSeconds = pageCount * 10;
              // Show toast only if processing estimated to be >2 minutes
              if (estimatedSeconds > 120) {
                const minMinutes = Math.ceil(estimatedSeconds / 60);
                const maxMinutes = Math.ceil((estimatedSeconds * 1.2) / 60);
                addToast(
                  'info',
                  `Large PDFs can take time to process. Expect ${minMinutes}–${maxMinutes} minutes for processing.`,
                );
              }
            } catch (err) {
              // ignore errors when estimating pages
            }
          }

          const uploaded = await uploadToStorage({
            companyId: questionnaire?.company_id!,
            documentType: 'bulk_document',
            file,
            path: `/${path}`,
            questionnaireId: questionnaire?.id!,
            requiresSplitting: split,
            token,
          });

          if (uploaded?.error) {
            addToast('error', uploaded.error);
            continue;
          }

          if (uploaded.is_duplicate) {
            const fileName = file?.name || 'Document';
            addToast(
              'error',
              `${fileName} has already been uploaded in this questionnaire. It will not be uploaded again.`,
            );
            await reloadQuestionnairePreview(undefined, undefined, true);
          }

          if (uploaded.document_name) {
            documentNamesBeingProcessed.push(uploaded.document_name);
          }

          // TODO: posthog
          // posthog.capture('uploaded document', {
          //   source: 'questionnaire bulk upload',
          // });
        }

        if (documentNamesBeingProcessed.length > 0) {
          await refreshQuestionnaireUntilNoDocumentsAreProcessing(
            1000,
            documentNamesBeingProcessed,
          );
        }
      } catch (error) {
        addToast(
          'error',
          'An error occurred while uploading the document(s).',
          { error },
        );
      } finally {
        setUploading(false);
      }
    },
    [
      addToast,
      token,
      reloadQuestionnairePreview,
      questionnaire,
      refreshQuestionnaireUntilNoDocumentsAreProcessing,
      setUploading,
    ],
  );

  const onProceed = (value: Array<any>) => {
    onUpload(value);
    onClose();
  };

  return (
    <Dialog
      isNarrow
      isOpen={isOpen}
      onClose={onClose}
      title="Multi-Form Upload Confirmation"
    >
      <Dialog.Content>
        <Flex isColumn={true} gap={6}>
          <Text>
            Do the documents you intend to upload require automatic splitting
            (contains more than one document)?
          </Text>
          <Flex>
            <table className="sorabanTable">
              <thead>
                <tr>
                  <td>
                    <Heading level={7}>Document name</Heading>
                  </td>
                  <td>
                    <Heading level={7}>Split?</Heading>
                  </td>
                </tr>
              </thead>
              <tbody>
                {Object.values(filesAsHash).map(({ file, split }: any) => (
                  <tr key={file.name}>
                    <td className={styles.tableData}>{file.name}</td>
                    <td>
                      <Checkbox
                        isChecked={split}
                        label=""
                        isDisabled={!file.name.toLowerCase().endsWith('.pdf')}
                        helperText={
                          !file.name.toLowerCase().endsWith('.pdf')
                            ? 'This document is not a PDF'
                            : undefined
                        }
                        onChange={(e) => {
                          setFilesAsHash((prev: any) => {
                            prev[file.name].split = e.target.checked;
                            return { ...prev };
                          });
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Flex>
        </Flex>
      </Dialog.Content>
      <Dialog.Footer>
        <ButtonGroup align="right">
          <Button
            intent="danger-secondary"
            onClick={onClose}
            type="button"
            value="Cancel"
          />
          <Button
            intent="primary"
            onClick={() => onProceed(Object.values(filesAsHash))}
            type="button"
            value="Proceed"
          />
        </ButtonGroup>
      </Dialog.Footer>
    </Dialog>
  );
};
